/**
 * Google tag (gtag.js) de la app · GA4 sin GTM (bloque 2 · G1). Espejo de `landing/google-tag.js`
 * (la landing no puede importar de `src/`); el test `googleTag` exige que las constantes
 * coincidan y que `importeDeSesion` sea letra por letra la de `metaPixel.js`.
 *
 * APAGADO hasta que exista `GA4_MEASUREMENT_ID`. Un solo ID para pux.app y app.pux.app
 * (`cookie_domain: 'auto'` → `_ga` en `.pux.app`, sin linker). Consent Mode v2 ANTES de
 * cargar el script: `granted` fuera de UE/EEE/UK/CH salvo `ad_personalization` (siempre
 * `denied`); `denied` dentro. Ver el comentario largo en `landing/google-tag.js`.
 *
 * Los eventos van por `eventoGoogle(nombre, params)` y los parámetros por una lista blanca:
 * nunca un correo, un teléfono, un nombre ni un uid. `page_view` lo manda gtag solo, también
 * en los cambios de ruta de la SPA (medición mejorada de GA4).
 */

const GA4_MEASUREMENT_ID = 'G-QKGGMDFMBL';   // propiedad «PUX.app», flujo «PUX web» (7-oct-2026)
const HOSTS_AUTORIZADOS = Object.freeze(['pux.app', 'www.pux.app', 'app.pux.app']);
// UE-27 + Islandia, Liechtenstein, Noruega (EEE) + Reino Unido + Suiza.
const REGIONES_DE_CONSENTIMIENTO_PREVIO = Object.freeze(['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'GB', 'CH']);
const PARAMETROS_PERMITIDOS = Object.freeze(['plan', 'value', 'currency', 'transaction_id', 'necesidad', 'idioma', 'method', 'coupon', 'discount']);

export function parametrosPermitidos(params) {
  const out = {};
  if (!params || typeof params !== 'object') return out;
  for (const k of PARAMETROS_PERMITIDOS) {
    const v = params[k];
    // `value` y `discount` son importes: sólo numéricos. El resto, texto corto.
    // OJO: si `discount` cayera en la rama de texto, la lista blanca lo BORRARÍA en silencio.
    if (k === 'value' || k === 'discount') { if (typeof v === 'number' && Number.isFinite(v)) out[k] = v; }
    else if (typeof v === 'string' && v) out[k] = v.slice(0, 100);
  }
  return out;
}

function hostActual() {
  try { return window.location.hostname; } catch (err) { return ''; }
}

export function esHostAutorizado(hostname) {
  return typeof hostname === 'string' && HOSTS_AUTORIZADOS.includes(hostname.toLowerCase());
}

/** Inyecta gtag.js una vez, con los defaults de consentimiento delante. `false` si está apagado o fuera de producción. */
export function cargarGoogleTag() {
  if (typeof window === 'undefined' || !GA4_MEASUREMENT_ID) return false;
  if (!esHostAutorizado(hostActual())) return false;
  if (window.__puxGoogleCargado) return true;
  window.__puxGoogleCargado = true;
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('consent', 'default', { ad_storage: 'granted', analytics_storage: 'granted', ad_user_data: 'granted', ad_personalization: 'denied' });
  gtag('consent', 'default', { ad_storage: 'denied', analytics_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied', region: [...REGIONES_DE_CONSENTIMIENTO_PREVIO] });
  gtag('js', new Date());
  gtag('config', GA4_MEASUREMENT_ID, { cookie_domain: 'auto', allow_google_signals: false, allow_ad_personalization_signals: false });
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(GA4_MEASUREMENT_ID);
  const primero = document.getElementsByTagName('script')[0];
  if (primero && primero.parentNode) primero.parentNode.insertBefore(s, primero); else document.head.appendChild(s);
  return true;
}

/** Un evento de GA4. NUNCA lanza; `false` si el tag no está cargado. */
export function eventoGoogle(nombre, params) {
  try {
    if (typeof window === 'undefined' || !window.__puxGoogleCargado || typeof window.gtag !== 'function') return false;
    window.gtag('event', nombre, parametrosPermitidos(params));
    return true;
  } catch (err) { return false; }
}

/**
 * LA FÓRMULA DEL IMPORTE — copia letra por letra de `importeDeSesion` en `metaPixel.js` (y de
 * `metaCapi.js`): `purchase` y `begin_checkout` llevan el mismo `value`/`currency` que Meta.
 */
export function importeDeSesion(amountTotalCents, currency) {
  const value = (typeof amountTotalCents === 'number' && Number.isFinite(amountTotalCents))
    ? amountTotalCents / 100
    : null;
  const cur = (typeof currency === 'string' && currency.trim())
    ? currency.trim().toUpperCase()
    : null;
  return { value, currency: cur };
}

function paramsDeImporte(amountTotalCents, currency) {
  const i = importeDeSesion(amountTotalCents, currency);
  const p = {};
  if (i.value !== null) p.value = i.value;
  if (i.currency !== null) p.currency = i.currency;
  return p;
}

/**
 * LA FÓRMULA DEL DESCUENTO — copia letra por letra de `descuentoDeSesion` en
 * `src/utils/metaPixel.js` y equivalente a la de `functions/integrations/utils/metaCapi.js`.
 * Misma razón que el importe: Meta se queda con la mitad que llega PRIMERO y no fusiona, así
 * que si las dos mandaran `coupon` distinto, el cupón reportado sería el de la carrera.
 *
 * NO cambia la semántica de `value`, que sigue siendo el importe COBRADO (0 con un cupón del
 * 100 %). `discount` y `coupon` existen para que ese 0 sea explicable y no parezca una rotura.
 *
 * `discount: null` cuando no hubo descuento —y también cuando fue de 0 céntimos—: "no hubo
 * descuento" no es "descuento de 0", y el parámetro se omite en vez de mandar ruido.
 * Los dos salen de `checkoutConfirmado`, que es la ÚNICA fuente: el navegador no dispara sin
 * esa confirmación y la CAPI la lee del mismo documento, así que no pueden divergir.
 */
export function descuentoDeSesion(amountDiscountCents, promoCode) {
  const discount = (typeof amountDiscountCents === 'number' && Number.isFinite(amountDiscountCents) && amountDiscountCents > 0)
    ? amountDiscountCents / 100
    : null;
  const cupon = (typeof promoCode === 'string' && promoCode.trim())
    ? promoCode.trim().slice(0, 100)
    : null;
  return { discount, coupon: cupon };
}

/** Sólo entra en el evento lo que tiene valor: nunca `discount: null` ni `coupon: null`. */
function paramsDeDescuento(amountDiscountCents, promoCode) {
  const d = descuentoDeSesion(amountDiscountCents, promoCode);
  const p = {};
  if (d.discount !== null) p.discount = d.discount;
  if (d.coupon !== null) p.coupon = d.coupon;
  return p;
}

/** Con qué entró la persona: `google` (popup) o `email` (código por correo → custom token, sin providerData). */
export function metodoDeAcceso(user) {
  const ids = Array.isArray(user && user.providerData) ? user.providerData.map((p) => p && p.providerId) : [];
  if (ids.includes('google.com')) return 'google';
  return 'email';
}

/** `sign_up` · sólo lo llama `acceptConsent` en la primera aceptación con contexto `alta`. */
export function signUp({ method }) {
  return eventoGoogle('sign_up', { method });
}

/** `begin_checkout` · justo donde Meta manda `InitiateCheckout`: la sesión de Stripe existe. */
export function beginCheckout({ amountTotal, currency, plan }) {
  return eventoGoogle('begin_checkout', { ...paramsDeImporte(amountTotal, currency), plan });
}

/**
 * `purchase` · una vez por sesión de Stripe, con `transaction_id` = session.id (GA4 deduplica
 * por él). Misma guarda y mismo criterio que `purchaseUnaVez` de Meta: sin almacenamiento, no
 * se dispara (perder un evento es barato; duplicar una compra falsea ingresos).
 */
export function purchaseGoogleUnaVez(sessionId, confirmacion) {
  if (!sessionId) return false;
  if (!confirmacion || confirmacion.sessionId !== sessionId) return false;
  const marca = 'pux_ga_purchase_sent:' + sessionId;
  try {
    if (window.sessionStorage.getItem(marca)) return false;
    window.sessionStorage.setItem(marca, '1');
  } catch (err) {
    return false;
  }
  return eventoGoogle('purchase', {
    transaction_id: sessionId,
    ...paramsDeImporte(confirmacion.amountTotal, confirmacion.currency),
    ...paramsDeDescuento(confirmacion.amountDiscount, confirmacion.promoCode),
  });
}

export { GA4_MEASUREMENT_ID, HOSTS_AUTORIZADOS, REGIONES_DE_CONSENTIMIENTO_PREVIO, PARAMETROS_PERMITIDOS };
